import React, {useState, useEffect, useCallback} from 'react';
import {useParams, Link, useNavigate, useLocation} from 'react-router-dom';
import {
  Star,
  ArrowLeft,
  Instagram,
  Youtube,
  DollarSign,
  MessageCircle,
  Calendar,
  Clock,
  Check,
  MapPin,
  LanguagesIcon,
} from 'lucide-react';
import {doc, getDoc, collection, query, where, getDocs} from 'firebase/firestore';
import {db} from '../firebase';
import {cn} from '../util/designUtil';
import {formatNumber} from '../util/formatters';
import BookingModal from '../components/BookingModal';
import PostComposer from '../components/PostComposer';
import CreatorTimeline from '../components/CreatorTimeline';
import SendGiftCard from '../components/SendGiftCard';
import { useAuth } from '../contexts/AuthContext';
import { resolveProfileIdentifier } from '../util/profileSlugUtils';

interface StarProfile {
  id: string;
  name: string;
  bio: string;
  about?: string;
  imageUrl: string;
  instagramVerification?: {
    username: string;
    verified: boolean;
  };
  youtubeVerification?: {
    channelId: string;
    verified: boolean;
    channelName?: string;
  };
  tiktokVerification?: {
    username: string;
    verified: boolean;
  };
  rating?: number;
  reviewsCount?: number;
  followersCount?: string;
  tags?: string[];
  location?: string;
  languages?: string[];
  rate?: string;
  minCallDuration?: number;
  maxCallDuration?: number;
  achievements?: string[];
  availableDays?: string[];
}

function TikTokIcon({className}: {className?: string}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('lucide lucide-tiktok', className)}>
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  );
}

const mockStarDetails = {
  categories: ['lifestyle', 'fashion', 'travel'],
  rate: '$10/min',
  followers: '2.5M',
  rating: 4.9,
  reviewsCount: 128,
  achievements: [
    'Featured in Vogue Magazine',
    'Collaborated with major fashion brands',
    'Award-winning content creator',
  ],
  languages: ['English', 'Spanish'],
  location: 'Los Angeles, CA',
  availabilityStatus: 'Available',
};



const Badge: React.FC<{
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'secondary';
}> = ({children, className, variant = 'default'}) => (
  <div
    className={cn(
      'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
      variant === 'default'
        ? 'border-transparent bg-purple-600 text-purple-50 hover:bg-purple-600/80'
        : 'border-transparent bg-gray-200 text-gray-700 hover:bg-gray-200/80',
      className,
    )}>
    {children}
  </div>
);

const Card: React.FC<{children: React.ReactNode; className?: string}> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      'rounded-lg border bg-white text-gray-900 shadow-sm',
      className,
    )}>
    {children}
  </div>
);
const CardContent: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({children, className}) => (
  <div className={cn('p-6 pt-0', className)}>{children}</div>
);
const CardHeader: React.FC<{children: React.ReactNode; className?: string}> = ({
  children,
  className,
}) => (
  <div className={cn('flex flex-col space-y-1.5 p-6', className)}>
    {children}
  </div>
);

const Tabs: React.FC<{children: React.ReactNode; className?: string}> = ({
  children,
  className,
}) => <div className={cn(className)}>{children}</div>;

const TabsList: React.FC<{children: React.ReactNode; className?: string}> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      'inline-flex h-10 items-center justify-center rounded-md bg-gray-100 p-1 text-gray-600 border-b',
      className,
    )}>
    {children}
  </div>
);

const TabsTrigger: React.FC<{
  children: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  className?: string;
}> = ({children, isActive, onClick, className}) => (
  <button
    onClick={onClick}
    className={cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
      isActive
        ? 'bg-white text-purple-700 shadow-sm font-semibold'
        : 'hover:bg-gray-200 hover:text-gray-800',
      className,
    )}>
    {children}
  </button>
);

const TabsContent: React.FC<{
  children: React.ReactNode;
  className?: string;
  isActive: boolean;
}> = ({children, className, isActive}) => (
  <div
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      !isActive && 'hidden',
      className,
    )}>
    {children}
  </div>
);

const StarProfileScreen: React.FC = () => {
  const {starId} = useParams();
  const {user: authUser, setUser} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<StarProfile | null>(null);
  const [activeTab, setActiveTab] = useState<
    'about' | 'instagram' | 'youtube' | 'tiktok'
  >('about');
  const [loading, setLoading] = useState(true);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [hasAvailableSlots, setHasAvailableSlots] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!starId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const resolvedUid = await resolveProfileIdentifier(starId);
        const docRef = doc(db, 'users', resolvedUid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setProfile({
            id: docSnap.id,
            ...docSnap.data(),
            rating: data.rating || 0,
            reviewsCount: data.reviewsCount || 0,
            followersCount: data.followersCount || '',
            tags: data.tags || [],
            location: data.location || 'Los Angeles, CAA',
            languages: data.languages || [],
            rate: data.pricePerMinute ?? null,
            minCallDuration: data.minCallDuration || 5,
            maxCallDuration: data.maxCallDuration || 60,
            achievements: data.achievements || [],
            availableDays: data.availableDays || [],
          } as StarProfile);
          console.log(data);

          // Set default active tab based on available verifications
          if (docSnap.data().instagramVerification?.verified) {
            setActiveTab('instagram');
          } else if (docSnap.data().youtubeVerification?.verified) {
            setActiveTab('youtube');
          } else if (docSnap.data().tiktokVerification?.verified) {
            setActiveTab('tiktok');
          }

          // Check if star has available slots
          try {
            const now = new Date();
            const slotsQuery = query(
              collection(db, 'slots'),
              where('starId', '==', resolvedUid),
              where('startTime', '>=', now),
            );
            const slotsSnap = await getDocs(slotsQuery);
            const hasFree = slotsSnap.docs.some(slotDoc => {
              const slotData = slotDoc.data();
              const bookings = slotData.bookings || [];
              const slotStart = slotData.startTime.toDate();
              const slotEnd = slotData.endTime.toDate();
              const totalMinutes = (slotEnd.getTime() - slotStart.getTime()) / 60000;
              const bookedMinutes = bookings.reduce((sum: number, b: any) => {
                const bStart = b.startTime.toDate ? b.startTime.toDate() : new Date(b.startTime);
                const bEnd = b.endTime.toDate ? b.endTime.toDate() : new Date(b.endTime);
                return sum + (bEnd.getTime() - bStart.getTime()) / 60000;
              }, 0);
              return bookedMinutes < totalMinutes;
            });
            setHasAvailableSlots(hasFree);
          } catch {
            setHasAvailableSlots(false);
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [starId]);

  const getVerifiedPlatforms = () => {
    if (!profile) return [];
    const platforms: ('instagram' | 'youtube' | 'tiktok')[] = [];
    if (profile.instagramVerification?.verified) platforms.push('instagram');
    if (profile.youtubeVerification?.verified) platforms.push('youtube');
    if (profile.tiktokVerification?.verified) platforms.push('tiktok');
    return platforms;
  };

  const verifiedSocialPlatforms = getVerifiedPlatforms();

  const handleBookSessionClick = () => {
    if (!authUser) {
      navigate('/login', {state: {from: location}});
      return;
    }
    if (profile && profile.id) {
      setIsBookingModalOpen(true);
    } else {
      // This case should ideally not be hit if the button is rendered with a profile
      console.error("Booking cannot proceed: profile data is missing.");
      // Optionally, show a toast or alert to the user
    }
  };

  // The backend debits users/{fanId}.credits inside its own transaction, so the
  // stored balance is the authoritative one; subtracting locally is only the
  // fallback for a read that fails, as BookingModal does after a booking.
  const handleGiftSent = useCallback(
    async (credits: number) => {
      if (!authUser) {
        return;
      }
      let next = (authUser.credits ?? 0) - credits;
      try {
        const snap = await getDoc(doc(db, 'users', authUser.uid));
        const stored = snap.data()?.credits;
        if (typeof stored === 'number') {
          next = stored;
        }
      } catch (error) {
        console.error('Could not refresh credits after a gift:', error);
      }
      setUser({...authUser, credits: next});
    },
    [authUser, setUser],
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center">
          <svg
            className="animate-spin h-8 w-8 text-purple-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-700 mt-2">Loading Profile...</span>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-100 p-4">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            Profile Not Found
          </h2>
          <p className="text-gray-500 mb-6">
            We couldn't find the profile you were looking for.
          </p>
          <Link
            to="/search"
            className="inline-flex items-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Search
          </Link>
        </div>
      </div>
    );
  }

  // profile.id is the resolved user id, so slug URLs match too.
  const isOwnProfile = !!authUser && authUser.uid === profile.id;

  const socialLinks = [
    profile.instagramVerification?.verified && {
      platform: 'instagram',
      user: profile.instagramVerification.username,
      Icon: Instagram,
      urlPrefix: 'https://instagram.com/',
    },
    profile.youtubeVerification?.verified && {
      platform: 'youtube',
      user: profile.youtubeVerification.channelId,
      name: profile.youtubeVerification.channelName,
      Icon: Youtube,
      urlPrefix: `https://youtube.com/channel/`,
    },
    profile.tiktokVerification?.verified && {
      platform: 'tiktok',
      user: profile.tiktokVerification.username,
      Icon: TikTokIcon,
      urlPrefix: 'https://tiktok.com/@',
    },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 text-gray-800">
      {/* Mobile Sticky Booking Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 p-4 z-20 md:hidden shadow-xl">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm text-gray-500">Starting at</div>
            <div className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {profile.rate != null ? `$${Number(profile.rate).toFixed(2)}/min` : 'Price not set'}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {profile.minCallDuration || 5}–{profile.maxCallDuration || 60} min per session
            </div>
          </div>
          <button
            onClick={handleBookSessionClick}
            disabled={!profile || profile.rate == null || !authUser}
            className="flex-1 max-w-xs h-12 text-base font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg rounded-lg flex items-center justify-center gap-2 disabled:opacity-50">
            <Calendar className="h-5 w-5" />
            Book Session
          </button>
        </div>
      </div>

      <main className="max-w-7xl mx-auto py-6 sm:py-8 px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20 md:mb-0">
          {/* Left Column - Original Content */}
          <div className="lg:col-span-2">
            <div className="bg-white p-5 sm:p-6 md:p-8 rounded-xl shadow-lg border border-gray-200">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="w-full md:w-[280px] lg:w-[300px] flex-shrink-0 relative">
                  {profile.imageUrl ? (
                    <img
                      src={profile.imageUrl}
                      alt={profile.name}
                      className="rounded-lg w-full aspect-square object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="rounded-lg w-full aspect-square bg-gray-200 flex items-center justify-center border border-gray-300">
                      <span className="text-gray-500 text-3xl font-medium">
                        {profile.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  {profile.followersCount && (
                    <div className="absolute top-2.5 right-2.5 bg-black bg-opacity-60 text-white px-2.5 py-1 rounded-md text-xs font-semibold shadow">
                      {profile.followersCount} followers
                    </div>
                  )}
                </div>

                <div className="w-full flex flex-col space-y-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                      {profile.name}
                    </h1>
                    {profile.rating && typeof profile.reviewsCount === 'number' ? (
                      <div className="flex items-center gap-1.5 text-sm mt-1 sm:mt-0 flex-shrink-0">
                        <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                        <span className="font-semibold text-gray-800">
                          {profile.rating.toFixed(1)}
                        </span>
                        <span className="text-gray-600">
                          ({formatNumber(profile.reviewsCount)} reviews)
                        </span>
                      </div>
                    ) : (
                      <div></div>
                    )}
                  </div>

                  {profile.tags && profile.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {profile.tags.map((cat: string) => (
                        <span
                          key={cat}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-300">
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}

                  {profile.location && (
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-1.5 text-gray-500" />
                      <span>{profile.location}</span>
                    </div>
                  )}

                  <p className="text-gray-700 leading-relaxed text-sm">
                    {profile.bio || 'No bio available.'}
                  </p>

                  <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-6 gap-y-3 text-sm pt-1">
                    {profile.rate && (
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 text-green-600 mr-1" />
                        <span className="text-gray-800 font-semibold">
                          {Number(profile.rate).toFixed(2)}
                        </span>
                        <span className="text-gray-600 ml-1">per minute</span>
                      </div>
                    )}
                    {profile.languages && profile.languages.length > 0 && (
                      <div className="flex items-center text-gray-600">
                        <MessageCircle className="h-4 w-4 mr-1.5 text-gray-500" />
                        <span>Languages: {profile.languages.join(', ')}</span>
                      </div>
                    )}
                  </div>

                  {socialLinks.length > 0 && (
                    <div className="flex flex-wrap gap-2.5 pt-2">
                      {socialLinks.map(link => {
                        if (!link) return null;
                        const {platform, user, name, Icon, urlPrefix} = link as any;

                        let platformSpecificClass = '';
                        switch (platform) {
                          case 'instagram':
                            platformSpecificClass =
                              'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white';
                            break;
                          case 'youtube':
                            platformSpecificClass =
                              'bg-red-600 hover:bg-red-700 text-white';
                            break;
                          case 'tiktok':
                            platformSpecificClass =
                              'bg-black hover:bg-gray-800 text-white';
                            break;
                          default:
                            platformSpecificClass =
                              'bg-gray-200 hover:bg-gray-300 text-gray-800';
                        }

                        let baseButtonClass =
                          'inline-flex items-center justify-center rounded-full text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background';
                        let paddingClass = 'px-3 py-1.5';
                        let iconClass = 'h-3.5 w-3.5';

                        let displayText = name || `@${user}`;
                        if (platform === 'youtube' && name)
                          displayText = name;
                        else if (platform === 'youtube' && !name)
                          displayText = user;
                        else displayText = `@${user}`;

                        return (
                          <a
                            key={platform}
                            href={`${urlPrefix}${user}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${baseButtonClass} ${platformSpecificClass} ${paddingClass} gap-1.5`}>
                            <Icon className={iconClass} />
                            {displayText}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Divider and spacing before tabs */}
              <div className="mt-8 pt-8 border-t border-gray-200"></div>

          <Tabs>
            <TabsList className="mb-6">
              <TabsTrigger
                isActive={activeTab === 'about'}
                onClick={() => setActiveTab('about')}>
                About
              </TabsTrigger>
              {verifiedSocialPlatforms.map(platform => (
                <TabsTrigger
                  key={platform}
                  isActive={activeTab === platform}
                  onClick={() => setActiveTab(platform)}>
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent isActive={activeTab === 'about'}>
              <Card className="mb-6">
                <CardHeader>
                  <h2 className="text-xl font-semibold text-gray-900">
                    About {profile.name}
                  </h2>
                </CardHeader>
                <CardContent className="space-y-5 text-gray-700 text-sm">
                  <p className="leading-relaxed">
                    {profile.about || profile.bio || 'No information available about this influencer yet.'}
                  </p>

                  {profile.achievements && profile.achievements.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1.5">
                        Achievements
                      </h3>
                      <ul className="list-disc list-inside space-y-1 pl-1">
                        {profile.achievements.map((ach: string) => (
                          <li key={ach}>{ach}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1.5">
                      Details
                    </h3>
                    <ul className="space-y-1.5">
                      {profile.languages && profile.languages.length > 0 && (
                        <li>
                          <strong>Languages:</strong>{' '}
                          {profile.languages.join(', ')}
                        </li>
                      )}
                      {profile.location && (
                        <li>
                          <strong>Location:</strong> {profile.location}
                        </li>
                      )}
                      {profile.availableDays &&
                        profile.availableDays.length > 0 && (
                          <li>
                            <strong>Availability:</strong>{' '}
                            {profile.availableDays.join(', ')}
                          </li>
                        )}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>


            {verifiedSocialPlatforms.map(platform => (
              <TabsContent key={platform} isActive={activeTab === platform}>
                <Card>
                  <CardHeader>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {platform.charAt(0).toUpperCase() + platform.slice(1)}{' '}
                      Feed
                    </h2>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-lg overflow-hidden border border-gray-200 aspect-[16/10] sm:aspect-[16/9]">
                      {platform === 'instagram' &&
                        profile.instagramVerification?.username && (
                          <iframe
                            title={`${profile.name} Instagram Feed`}
                            src={`https://www.instagram.com/${profile.instagramVerification.username}/embed`}
                            className="w-full h-full border-0"
                            allowTransparency
                          />
                        )}
                      {platform === 'youtube' &&
                        profile.youtubeVerification?.channelId && (
                          <iframe
                            title={`${profile.name} YouTube Videos`}
                            src={`https://www.youtube.com/embed/videoseries?list=UU${profile.youtubeVerification.channelId.substring(
                              2,
                            )}`}
                            className="w-full h-full border-0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        )}
                      {platform === 'tiktok' &&
                        profile.tiktokVerification?.username && (
                          <iframe
                            title={`${profile.name} TikTok Feed`}
                            src={`https://www.tiktok.com/embed/user/@${profile.tiktokVerification.username}`}
                            className="w-full h-full border-0 bg-white"
                            allow="autoplay; encrypted-media;"
                            allowFullScreen
                            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
                          />
                        )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
            </div>

            {isOwnProfile && (
              <div className="mt-6">
                <PostComposer creatorId={profile.id} />
              </div>
            )}

            {!isOwnProfile && (
              <div className="mt-6">
                <SendGiftCard
                  creatorId={profile.id}
                  creatorName={profile.name}
                  onGiftSent={handleGiftSent}
                />
              </div>
            )}

            <div className="mt-6">
              <CreatorTimeline
                creatorId={profile.id}
                creatorName={profile.name}
                isOwnProfile={isOwnProfile}
              />
            </div>
          </div>

          {/* Right Column - Booking Sidebar (Sticky) */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 hidden md:block">
              <Card className="border-2 border-purple-300 shadow-xl">
                <CardContent className="p-6 space-y-6">
                  <div className="text-center pb-4 border-b border-gray-200">
                    <div className="text-sm text-gray-500 mb-2">Book a session with {profile.name}</div>
                    <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                      {profile.rate != null ? `$${Number(profile.rate).toFixed(2)}/min` : 'Price not set'}
                    </div>
                    {profile.rate != null && (
                      <div className="text-sm text-gray-600">per minute</div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Clock className="h-5 w-5 text-gray-600" />
                      <div>
                        <div className="text-xs text-gray-500">Minimum session</div>
                        <div className="font-medium">{profile.minCallDuration || 5} minutes</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Clock className="h-5 w-5 text-gray-600" />
                      <div>
                        <div className="text-xs text-gray-500">Maximum session</div>
                        <div className="font-medium">{profile.maxCallDuration || 60} minutes</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <DollarSign className="h-5 w-5 text-gray-600" />
                      <div>
                        <div className="text-xs text-gray-500">Platform fee</div>
                        <div className="font-medium">5% or $5 (whichever is lower)</div>
                      </div>
                    </div>

                    {hasAvailableSlots ? (
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <Check className="h-5 w-5 text-green-600" />
                        <div>
                          <div className="text-xs text-green-600 font-medium">Available for booking</div>
                          <div className="text-xs text-green-700">Secure your slot in seconds</div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <Clock className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="text-xs text-gray-500 font-medium">Currently unavailable</div>
                          <div className="text-xs text-gray-400">No open time slots</div>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleBookSessionClick}
                    disabled={!profile || profile.rate == null || !authUser}
                    className="w-full h-14 text-lg font-semibold shadow-lg hover:shadow-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                    <Calendar className="h-5 w-5" />
                    Book a Session
                  </button>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-xs text-gray-500 text-center space-y-1">
                      <p>✓ Secure payment processing</p>
                      <p>✓ Cancel up to 24 hours before</p>
                      <p>✓ Instant confirmation</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {isBookingModalOpen && profile && profile.id && (
        <BookingModal
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
          starId={profile.id}
        />
      )}
    </div>
  );
};

export default StarProfileScreen;
